import debounce from 'debounce-promise';
import { makeAutoObservable } from 'mobx';
import type { RequireAllProps } from '../../../store/utils/Dto';
import extractValue, { EventOrValue } from '../../../store/utils/extractValue';
import { errorHandler } from '../../../utils/errors';
import type { Role } from '../../users/store/User';
import Configurations, { type ConfigurationsDto } from './Configurations';
import ServerFeatureFlags, {
  type ServerFeatureFlagsDto,
} from './ServerFeatureFlags';
import type { AccountSaveArgs, AccountSaveMethod } from './types';

export default class Account {
  id = '';
  email = '';
  name = '';
  role: Role = 'CUSTOMER';
  configurations: Configurations;
  serverFeatureFlags: ServerFeatureFlags;

  protected readonly saveDelayed = debounce(this.save, 1000);

  constructor(
    { configurations = {} as any, featureFlags, ...dto }: AccountDto,
    private readonly onSave?: AccountSaveMethod,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    Object.assign(this, dto);

    this.configurations = new Configurations(configurations, this.saveDelayed);
    this.serverFeatureFlags = new ServerFeatureFlags(featureFlags || {});
  }

  get isWebhookEnabled(): boolean {
    return this.configurations.isWebhookEnabled;
  }

  get hasCdnEnabled(): boolean {
    return this.configurations?.cdn?.enabled;
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);
    Object.assign(this, { [name]: value });
  }

  handleChangeAndSave(e: EventOrValue) {
    this.handleChange(e);
    this.save().catch(errorHandler('Cannot change account configuration'));
  }

  toJSON(): RequireAllProps<AccountDto> {
    return {
      configurations: this.configurations.toJSON(),
      featureFlags: undefined,
      id: this.id,
      name: this.name || '',
      email: this.email,
      role: this.role,
    };
  }

  async save(options: AccountSaveArgs = {}) {
    await this.onSave?.({ account: this, ...options });
  }
}

export interface AccountDto {
  configurations?: ConfigurationsDto;
  email: string;
  featureFlags?: ServerFeatureFlagsDto;
  id: string;
  name?: string;
  role: string;
}
